import React, { useState } from 'react';
import { motion } from 'framer-motion';
import StatusBadge from './StatusBadge/StatusBadge';
import ActionButtons from './ActionButtons/ActionButtons';
import CustomCheckbox from '../../../../../../components/CustomCheckbox/CustomCheckbox';
import ProspectedBadge from './ProspectedBadge/ProspectedBadge';
import RatingStars from '../../../../../LeadsSalvos/components/LeadsTable/RatingStars/RatingStars';
import ProspectDetailModal from '../../../ProspectDetailModal/ProspectDetailModal';
import ConfirmationModal from '../../../../../../components/ConfirmationModal/ConfirmationModal';
import { Result } from '../../../../../../services/serpApi';
import { useTranslation } from 'react-i18next';
import { getWhatsAppLink } from '../../../../../../utils/whatsappUtils';
import { useSearchPersistence } from '../../../../../../hooks/useSearchPersistence';

interface ResultsTableRowProps {
  result: Result;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: (id: number) => void; // Nova prop para exclusão individual
  index: number;
}

const ResultsTableRow: React.FC<ResultsTableRowProps> = ({
  result,
  isSelected,
  onSelect,
  onDelete,
  index
}) => {
  const { t } = useTranslation();
  const { searchContext } = useSearchPersistence();
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleRowClick = (e: React.MouseEvent) => {
    // Previne que o clique na linha interfira se o clique for em um botão de ação
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input[type="checkbox"]')) {
      return;
    }
    onSelect();
  };

  const handleViewDetails = () => {
    setIsViewModalOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete(result.id);
    setIsDeleteModalOpen(false);
  };

  const rowClasses = isSelected
    ? 'bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 dark:hover:bg-primary/30'
    : 'hover:bg-gray-50 dark:hover:bg-background-dark-elevated2';

  // Função para truncar o endereço
  const truncateAddress = (address: string, maxLength: number = 40) => {
    if (!address) return 'N/A';
    if (address.length <= maxLength) return address;
    return address.substring(0, maxLength) + '...';
  };

  return (
    <>
      <motion.tr
        className={`transition-colors cursor-pointer ${rowClasses}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.3 }}
        whileHover={{ scale: 1.01 }}
        onClick={handleRowClick}
      >
        <td className="p-4">
          <CustomCheckbox
            checked={isSelected}
            onChange={onSelect}
          />
        </td>
        <td className="p-4 font-medium text-[#181411] dark:text-white">
          {result.empresa}
        </td>
        <td className="p-4 text-gray-600 dark:text-gray-200">
          {result.contato}
        </td>
        <td className="p-4 text-gray-600 dark:text-gray-200 max-w-xs">
          {/* Endereço truncado */}
          <span title={result.localizacao}>
            {truncateAddress(result.localizacao)}
          </span>
        </td>
        <td className="p-4 text-center">
          <StatusBadge status={result.status} whatsapp={result.whatsapp} />
        </td>
        <td className="p-4 text-center">
          <RatingStars rating={Math.round(result.rating || 0)} />
        </td>
        <td className="p-4 text-center">
          <ProspectedBadge prospected={result.prospected} />
        </td>
        <td className="p-4">
          <ActionButtons
            resultId={result.id}
            onView={handleViewDetails}
            onDelete={() => setIsDeleteModalOpen(true)}
            whatsappLink={getWhatsAppLink(result.phone, searchContext?.countryCode)}
          />
        </td>
      </motion.tr>

      <ProspectDetailModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        prospect={result}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('modals.confirm_delete_title')}
        message={t('Você tem certeza que deseja excluir este resultado da lista atual? Esta ação não pode ser desfeita.')}
        confirmText={t('Sim, Excluir')}
        cancelText={t('modals.cancel')}
      />
    </>
  );
};

export default ResultsTableRow;